import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
  type ReactNode,
} from 'react';
import { cn, Spinner } from '@skupreme/shared/ui';

/*
 * The app's legacy button API (Semantic-style props and variant names) drawn
 * in the kit's button look. Callers keep their imports; the colours are kit
 * tokens, so each variant matches the kit button closest to its old role.
 */

export type LegacyButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Semantic sizes: mini, tiny, small, medium, large, big, huge, massive. */
  size?: string;
  fluid?: boolean;
  loading?: boolean | number | null;
  /** `true` for an icon-only button; a node is rendered before the label. */
  icon?: boolean | ReactNode;
  positive?: boolean;
  negative?: boolean;
  primary?: boolean;
  basic?: boolean;
  compact?: boolean;
  /** Renders a link styled as the button, as Semantic's `href` did. */
  href?: string;
  target?: string;
  rel?: string;
};

const BASE = cn(
  'sk:m-0 sk:inline-flex sk:cursor-pointer sk:appearance-none sk:items-center sk:justify-center sk:gap-1.5',
  'sk:rounded-full sk:border-0 sk:px-4 sk:font-sans sk:text-dense sk:font-medium sk:tracking-dense sk:whitespace-nowrap sk:no-underline',
  'sk:transition-[background-color,box-shadow,border-color,color,opacity] sk:duration-75',
  'sk:outline-none sk:focus-visible:outline-2 sk:focus-visible:outline-offset-1 sk:focus-visible:outline-primary',
  'sk:disabled:cursor-not-allowed sk:disabled:opacity-50 sk:[&.disabled]:pointer-events-none sk:[&.disabled]:opacity-50',
  'sk:[&.flex-btn]:flex sk:[&.no-break]:whitespace-nowrap',
  'sk:[&.icon-btn]:size-10 sk:[&.icon-btn]:p-0 sk:[&.tiny.icon-btn]:size-7',
);

/* Semantic's size names, on the kit's height scale. */
const SIZE_CLASSES: Record<string, string> = {
  mini: 'sk:h-6.5 sk:px-3 sk:text-xs',
  tiny: 'sk:h-6.5 sk:px-3 sk:text-xs',
  small: 'sk:h-7',
  medium: 'sk:h-7',
  large: 'sk:h-9 sk:px-5',
  big: 'sk:h-10 sk:px-5',
  huge: 'sk:h-11 sk:px-6',
  massive: 'sk:h-12 sk:px-6',
};

const LegacyButton = forwardRef<HTMLButtonElement, LegacyButtonProps>(
  (
    {
      size,
      fluid,
      loading,
      icon,
      positive,
      negative,
      primary,
      basic,
      compact,
      href,
      target,
      rel,
      className,
      disabled,
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = cn(
      BASE,
      (size && SIZE_CLASSES[size]) || 'sk:h-7',
      size,
      compact && 'sk:px-3',
      fluid && 'sk:w-full',
      icon === true && 'sk:aspect-square sk:px-0',
      positive && 'sk:bg-success sk:text-surface',
      negative && 'sk:bg-danger sk:text-surface',
      primary && 'sk:bg-primary sk:text-surface',
      basic && 'sk:bg-transparent sk:inset-shadow-hairline',
      !!loading && 'sk:pointer-events-none sk:opacity-70',
      disabled && 'disabled',
      className,
    );
    const content = (
      <>
        {loading ? <Spinner size="sm" /> : icon !== true && icon}
        {children}
      </>
    );

    if (href) {
      /* Button-only attributes stay off the link. */
      const {
        type: _type,
        form: _form,
        formAction: _formAction,
        name: _name,
        value: _value,
        onClick,
        ...linkRest
      } = rest;
      return (
        <a
          {...(linkRest as AnchorHTMLAttributes<HTMLAnchorElement>)}
          href={disabled ? undefined : href}
          target={target}
          rel={rel}
          className={classes}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : linkRest.tabIndex}
          onClick={event => {
            /* As Semantic's link button: disabled blocks both. */
            if (disabled) {
              event.preventDefault();
              return;
            }
            (onClick as AnchorHTMLAttributes<HTMLAnchorElement>['onClick'])?.(
              event,
            );
          }}
        >
          {content}
        </a>
      );
    }

    return (
      <button {...rest} ref={ref} disabled={disabled} className={classes}>
        {content}
      </button>
    );
  },
);
LegacyButton.displayName = 'LegacyButton';

function variant(displayName: string, classes: string) {
  const Variant = forwardRef<HTMLButtonElement, LegacyButtonProps>(
    ({ className, ...props }, ref) => (
      <LegacyButton {...props} ref={ref} className={cn(classes, className)} />
    ),
  );
  Variant.displayName = displayName;
  return Variant;
}

// === Button Variants ===
/* `styled-button` marks it for the one parent rule that targets it. */
export const StyledButton = variant(
  'StyledButton',
  'styled-button sk:bg-primary sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
);

export const GreenButton = variant(
  'GreenButton',
  'sk:bg-success sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
);

export const BlueButton = variant(
  'BlueButton',
  'sk:bg-info sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
);

export const FlatButton = variant(
  'FlatButton',
  'sk:rounded-option sk:bg-transparent sk:text-fg sk:hover:bg-fill-quaternary',
);

export const StyleGreenHoverButton = variant(
  'StyleGreenHoverButton',
  'styled-button sk:bg-primary sk:text-surface sk:hover:bg-success sk:active:opacity-80',
);

// === BorderButton ===
interface BorderButtonProps extends Omit<LegacyButtonProps, 'color'> {
  backColor?: string;
  borderColor?: string;
  color?: string;
}

/*
 * An outlined button; `backColor`, `borderColor` and `color` override its
 * resting colours inline, while the hover stays black, as before.
 */
function borderButton(displayName: string, classes = '') {
  const Variant = forwardRef<HTMLButtonElement, BorderButtonProps>(
    ({ backColor, borderColor, color, className, style, ...props }, ref) => {
      const inline: CSSProperties = { ...style };
      if (backColor) inline.background = backColor;
      if (borderColor) inline.borderColor = borderColor;
      if (color) inline.color = color;

      return (
        <LegacyButton
          {...props}
          ref={ref}
          style={inline}
          className={cn(
            !backColor && 'sk:bg-surface',
            !borderColor && 'sk:inset-shadow-hairline sk:shadow-card',
            !color && 'sk:text-fg-secondary',
            'sk:hover:bg-fill-quaternary sk:hover:text-fg',
            'sk:[&.opacity-0]:pointer-events-none sk:[&.opacity-0]:-z-10 sk:[&.opacity-0]:opacity-0',
            classes,
            className,
          )}
        />
      );
    },
  );
  Variant.displayName = displayName;
  return Variant;
}

export const BorderButton = borderButton('BorderButton');

export const HoverBorderButton = BorderButton;

export const WhiteBorderButton = borderButton(
  'WhiteBorderButton',
  'sk:inset-shadow-[0_0_0_1px_var(--sk-surface)] sk:text-surface sk:hover:bg-surface sk:hover:text-fg',
);

export const DangerBorderButton = borderButton(
  'DangerBorderButton',
  'sk:inset-shadow-[0_0_0_1px_var(--sk-danger-fg)] sk:text-danger sk:hover:bg-danger-bg',
);

export const PurpleBorderButton = borderButton(
  'PurpleBorderButton',
  'sk:inset-shadow-[0_0_0_1px_var(--sk-accent-fg)] sk:text-accent sk:hover:bg-accent-bg',
);

export const GreenBorderButton = borderButton(
  'GreenBorderButton',
  'sk:inset-shadow-[0_0_0_1px_var(--sk-success-fg)] sk:text-success sk:hover:bg-success-bg',
);

export const LightBorderButton = borderButton(
  'LightBorderButton',
  /* For the coloured footer bars: clear fill, white ring and text. */
  'sk:bg-transparent sk:shadow-none sk:inset-shadow-[0_0_0_1px_var(--sk-surface)] sk:text-surface sk:hover:bg-surface sk:hover:text-fg',
);

export const WhiteBackgroundButton = borderButton(
  'WhiteBackgroundButton',
  'sk:bg-surface sk:text-fg sk:shadow-card sk:hover:bg-fill-quaternary',
);

// === More Button Variants ===
export const DangerButton = variant(
  'DangerButton',
  'sk:bg-danger sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
);

export const MuteButton = variant(
  'MuteButton',
  'sk:bg-fill-secondary sk:text-fg-secondary sk:hover:bg-fill-tertiary',
);

export const AccentButton = variant(
  'AccentButton',
  'sk:bg-success sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
);

export const SuccessButton = variant(
  'SuccessButton',
  cn(
    'sk:bg-success sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
    'sk:[&.catalog-merge]:inline-flex sk:[&.catalog-merge]:items-center',
    'sk:[&.assign-supplier]:bg-primary sk:[&.assign-supplier]:opacity-50 sk:[&.assign-supplier]:hover:opacity-100',
  ),
);

export const PurpleButton = variant(
  'PurpleButton',
  'sk:bg-accent sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
);

export const YellowButton = variant(
  'YellowButton',
  'sk:bg-warning sk:text-surface sk:hover:opacity-90 sk:active:opacity-80',
);

// === Links ===
/* Semantic's global `a` colours are unlayered, hence the `!`. */
type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  disabled?: boolean;
  /** Accepted for old callers; these are always anchors. */
  as?: 'a';
};

function link(displayName: string, classes: string, disabledClasses = '') {
  const Link = forwardRef<HTMLAnchorElement, LinkProps>(
    ({ disabled, as: _as, className, ...props }, ref) => (
      <a
        {...props}
        ref={ref}
        className={cn(
          'sk:cursor-pointer',
          classes,
          disabled && disabledClasses,
          className,
        )}
      />
    ),
  );
  Link.displayName = displayName;
  return Link;
}

export const StyledLink = link(
  'StyledLink',
  'sk:text-brand! sk:hover:text-brand!',
);

export const TextLink = link('TextLink', 'sk:text-fg! sk:hover:text-fg!');

export const NormalLink = link('NormalLink', '', 'sk:text-fg-tertiary!');

export const GreyLink = link(
  'GreyLink',
  'sk:text-fg-tertiary! sk:underline sk:hover:text-fg-tertiary!',
);

export const ActiveLink = link(
  'ActiveLink',
  'sk:text-xs sk:leading-[14px] sk:font-bold sk:text-fg! sk:hover:text-fg! sk:focus:text-fg!',
);

export const PrimaryLink = link(
  'PrimaryLink',
  'sk:text-link! sk:underline sk:visited:text-link! sk:hover:text-fg!',
  'sk:cursor-not-allowed sk:text-fg-tertiary!',
);

// === Hover Buttons ===
export const SuccessHoverButton = variant(
  'SuccessHoverButton',
  'sk:bg-transparent! sk:text-brand! sk:hover:bg-brand! sk:hover:text-white!',
);

export const PrimaryHoverButton = variant(
  'PrimaryHoverButton',
  'sk:bg-transparent! sk:text-fg! sk:hover:bg-primary! sk:hover:text-white!',
);

export const PrimaryLinkButton = variant(
  'PrimaryLinkButton',
  'sk:bg-transparent! sk:p-0! sk:text-link! sk:hover:bg-transparent! sk:hover:text-link!',
);

export const FlatButtonWithBorder = variant(
  'FlatButtonWithBorder',
  'sk:h-[42px] sk:rounded-[5px]! sk:border sk:border-solid sk:border-border! sk:bg-white! sk:hover:bg-[rgba(0,0,0,0.1)]!',
);

// === Button Components with Icons ===
interface IconButtonProps extends BorderButtonProps {
  text?: ReactNode;
  icon?: ReactNode;
}

export function BorderIconButton({
  text,
  icon,
  className = '',
  ...rest
}: IconButtonProps) {
  return (
    <BorderButton
      type="button"
      className={`sk:flex! sk:items-center! ${className}`}
      {...rest}
    >
      {icon}
      {text}
    </BorderButton>
  );
}

export function HoverBorderIconButton({
  text,
  icon,
  className = '',
  ...rest
}: IconButtonProps) {
  return (
    <HoverBorderButton
      type="button"
      className={`sk:flex! sk:items-center! ${className}`}
      {...rest}
    >
      {icon}
      {text}
    </HoverBorderButton>
  );
}
